import { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import axios, { CancelToken } from "axios";
import dayjs from "dayjs";
import { usePagination } from "hooks";
import transactionsDataMapper from "utils/transactionsDataMapper";
import { DATE_QUERY_FORMAT } from "constants/dateFormats";
import translate from "../../../../i18n/translate";
import {
  OPERATOR,
  OPERATIONS,
  OPERATIONS_EXPORT,
} from "../../../../constants/api";

const ALL_OPERATORS_ID = "ALL";

const ALL_OPERATORS = {
  id: ALL_OPERATORS_ID,
  name: translate("transactions.allOperators"),
  email: ALL_OPERATORS_ID,
};

const makeTransactionsRequest = async (
  source,
  range,
  limit,
  offset,
  selectedOperator,
  selectedChargeMethods,
  selectedCard,
  isExport
) => {
  const operatorParam =
    selectedOperator !== ALL_OPERATORS_ID ? { operator: selectedOperator } : {};

  const cardType = selectedCard ? { card_type: selectedCard } : {};

  const params = {
    start_date: range
      ? range.startDate.format(DATE_QUERY_FORMAT)
      : dayjs().format(DATE_QUERY_FORMAT),
    end_date: range
      ? range.endDate.format(DATE_QUERY_FORMAT)
      : dayjs().format(DATE_QUERY_FORMAT),
    ...operatorParam,
    ...cardType,
  };

  if (selectedChargeMethods.length) {
    params.payment_method = selectedChargeMethods.join(",");
  }

  const url = isExport ? OPERATIONS_EXPORT : OPERATIONS;

  if (!isExport) {
    params.limit = limit;
    params.offset = offset;
  }

  try {
    const response = await axios.get(url, {
      params,
      cancelToken: source.token, // this is just because the componet renders 2 times once we fix that issue we wont need this
    });

    return response;
  } catch (error) {
    return error;
  }
};

const makeOperatorsRequest = async (source) => {
  try {
    const response = await axios.get(OPERATOR, {
      cancelToken: source.token, // ditto
    });

    return response;
  } catch (error) {
    return error;
  }
};

// maybe split into useFilters useTable
const useTransactions = (range, username) => {
  const [loading, setLoading] = useState(true);
  const [transactionsData, setTransactionsData] = useState({});
  const { paginationConfig, setOffset, setPage } = usePagination(
    transactionsData?.total
  );

  const [operators, setOperators] = useState([]);
  const [downloadUrl, setDownloadUrl] = useState();

  // filters
  const [selectedChargeMethods, setSelectedChargeMethods] = useState([]);
  const [selectedOperator, setSelectedOperator] = useState(ALL_OPERATORS.email);
  const [selectedCard, setSelectedCard] = useState();

  const [selectedChargeMethodsTemp, setSelectedChargeMethodsTemp] = useState(
    selectedChargeMethods
  );
  const [selectedOperatorTemp, setSelectedOperatorTemp] =
    useState(selectedOperator);
  const [selectedCardTemp, setSelectedCardTemp] = useState(selectedCard);

  const intl = useIntl();

  const onDownloadClick = () => {
    setDownloadUrl(undefined);
  };

  const onExportClick = async () => {
    const source = CancelToken.source();
    const response = await makeTransactionsRequest(
      source,
      range,
      paginationConfig.limit,
      paginationConfig.offset,
      selectedOperator,
      selectedChargeMethods,
      selectedCard,
      true
    );

    if (response?.data?.success) {
      setDownloadUrl(response.data.result);
    } else {
      // how we should handle errors?
      console.error("Backend error", response);
    }
  };

  const onCheckboxChange = ({ target }) => {
    const { checked, name } = target;
    if (checked) {
      setSelectedChargeMethodsTemp([...selectedChargeMethodsTemp, name]);
    } else {
      const newValue = selectedChargeMethodsTemp.filter((id) => id !== name);
      setSelectedChargeMethodsTemp(newValue);
    }
  };

  const onCardClick = (cardId) => {
    if (cardId === selectedCardTemp) {
      setSelectedCardTemp();
    } else {
      setSelectedCardTemp(cardId);
    }
  };

  const onOperatorChange = (evt) => {
    setSelectedOperatorTemp(evt.target.value);
  };

  const onApplyFilters = () => {
    setSelectedChargeMethods(selectedChargeMethodsTemp);
    setSelectedOperator(selectedOperatorTemp);
    setSelectedCard(selectedCardTemp);

    setDownloadUrl(undefined);

    const source = CancelToken.source();
    getTransactionsData(source);
  };

  const onCleanFilters = () => {
    setDownloadUrl(undefined);
    setSelectedCard();
    setSelectedOperator(ALL_OPERATORS.email);
    setSelectedChargeMethods([]);
    setOffset(0);
    setPage(0);
  };

  const onFiltersOpen = () => {
    setSelectedOperatorTemp(selectedOperator);
    setSelectedChargeMethodsTemp(selectedChargeMethods);
    setSelectedCardTemp(selectedCard);
  };

  const getTransactionsData = async (source) => {
    setLoading(true);
    const response = await makeTransactionsRequest(
      source,
      range,
      paginationConfig.limit,
      paginationConfig.offset,
      selectedOperatorTemp,
      selectedChargeMethodsTemp,
      selectedCardTemp
    );

    if (response?.data?.success) {
      const data = response.data.result;
      setTransactionsData({
        rows: transactionsDataMapper(data, intl.formatDate, username),
        total: data.total,
      });
    } else {
      console.error("Backend error", response);
    }
    setLoading(false);
  };

  useEffect(() => {
    const source = CancelToken.source();

    const getData = async () => {
      const response = await makeOperatorsRequest(source);

      if (response?.data?.success) {
        const data = response.data.result;

        const operatorsData = [ALL_OPERATORS, ...data];
        setOperators(operatorsData);
      } else {
        console.error("Backend error", response);
      }
    };

    getData();

    return () => {
      source.cancel("Component unmounted");
    };
  }, []);

  useEffect(() => {
    const source = CancelToken.source();
    getTransactionsData(source);
    return () => {
      source.cancel("Component unmounted");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, paginationConfig.limit, paginationConfig.offset]);

  return {
    loading,
    transactions: transactionsData.rows,
    paginationConfig,
    getTransactionsData,
    filters: {
      downloadUrl,
      onApplyFilters,
      onCardClick,
      onCheckboxChange,
      onCleanFilters,
      onDownloadClick,
      onExportClick,
      onOperatorChange,
      onFiltersOpen,
      operators,
      selectedCard: selectedCardTemp,
      selectedChargeMethods: selectedChargeMethodsTemp,
      selectedOperator: selectedOperatorTemp,
    },
  };
};

export default useTransactions;
